// src/components/dashboard/Workshop3Dashboard.js
// Professional Workshop 3 Dashboard using Tremor layout + ECharts
import React, { useState } from 'react';
import {
  Card,
  Title,
  Text,
  Metric,
  Grid,
  Flex,
  Badge,
  BadgeDelta
} from '@tremor/react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  AlertTriangle,
  CheckCircle,
  Shield
} from 'lucide-react';
import EChart from './charts/EChart';
import DashboardGrid from './DashboardGrid';
import { W3_DEFAULT_LAYOUT } from './defaultLayouts';
import {
  ZONE_COLORS,
  ZONE_ORDER,
  TOP_INITIAL_LIMIT,
  resolveThresholds,
  resolveStakeholderThreatLevel,
  splitRetainedStakeholders,
  buildScatterPoints,
  thresholdLinePoints,
  buildInitialResidualRows,
  selectTopByInitial,
  sortByReductionDesc,
  buildZoneOriginBreakdown,
  buildPriorityStatusMatrix,
  computeSuggestedThresholds,
  buildEffectivenessProfiles,
  findOrphanMeasures
} from './charts/workshop3Data';

const Workshop3Dashboard = ({
  stakeholdersData = [],
  stakeholdersLoading = false,
  thresholds = null,
  measuresData = [],
  measuresLoading = false,
  attackPathsData = [],
  suggestedThresholds = null,
  editMode = false,
  layouts = null,
  onLayoutChange = null,
  onToggleHidden = null
}) => {
  const { t } = useTranslation();
  const [retainedOnly, setRetainedOnly] = useState(true);
  const [showAllResidual, setShowAllResidual] = useState(false);

  // Live thresholds: subscribes to threshold state, so adjusting thresholds
  // re-renders both the zone colors and the scatter markLines.
  const liveThresholds = resolveThresholds(thresholds);
  const suggested = suggestedThresholds || computeSuggestedThresholds(stakeholdersData);

  const { retained, retainedCount, totalCount } = splitRetainedStakeholders(stakeholdersData);
  const scopedStakeholders = retainedOnly ? retained : stakeholdersData;
  const measures = Array.isArray(measuresData) ? measuresData : [];
  const attackPaths = Array.isArray(attackPathsData) ? attackPathsData : [];

  const zoneLabel = (zone) => {
    if (zone === 'danger') {
      return t('dashboard.workshop3.dangerZone');
    }
    if (zone === 'control') {
      return t('dashboard.workshop3.controlZone');
    }
    if (zone === 'watch') {
      return t('dashboard.workshop3.watchZone');
    }
    return t('dashboard.workshop3.outsideZone');
  };
  const priorityLabel = (priority) => {
    if (priority === 'high') {
      return t('dashboard.workshop3.priorityHigh');
    }
    if (priority === 'medium') {
      return t('dashboard.workshop3.priorityMedium');
    }
    return t('dashboard.workshop3.priorityLow');
  };
  const statusLabel = (status) => {
    if (status === 'planned') {
      return t('dashboard.workshop3.statusPlanned');
    }
    if (status === 'in-progress') {
      return t('dashboard.workshop3.statusInProgress');
    }
    if (status === 'implemented') {
      return t('dashboard.workshop3.statusImplemented');
    }
    return t('dashboard.workshop3.statusAbandoned');
  };

  // KPI aggregates (retained scope, live thresholds)
  const totalStakeholders = scopedStakeholders.length;
  const internalCount = scopedStakeholders.filter((s) => s && s.type === 'internal').length;
  const externalCount = scopedStakeholders.filter((s) => s && s.type !== 'internal').length;
  const dangerCount = scopedStakeholders.filter(
    (s) => resolveStakeholderThreatLevel(s) >= liveThresholds.danger
  ).length;
  const categoryCount = new Set(
    scopedStakeholders.map((s) => String((s && s.category) || 'undefined'))
  ).size;
  const implementedCount = measures.filter(
    (m) => m && (m.implementation === 'implemented' || m.status === 'implemented')
  ).length;
  const orphanHealth = findOrphanMeasures(measures);

  // 3.1 Threshold scatter: x = exposition (D x P), y = fiabilite cyber (M x C),
  // dot size = threatLevel, color = zone off live thresholds.
  const scatterPoints = buildScatterPoints(scopedStakeholders, liveThresholds);
  const markLineSegments = [];
  ZONE_ORDER.slice(0, 3).forEach((zone) => {
    const livePoints = thresholdLinePoints(liveThresholds[zone]);
    if (livePoints) {
      markLineSegments.push({
        name: zoneLabel(zone),
        lineStyle: { color: ZONE_COLORS[zone], width: 2 },
        label: { formatter: zoneLabel(zone), color: ZONE_COLORS[zone], fontWeight: 600 },
        endpoints: livePoints
      });
    }
    if (suggested) {
      const suggestedPoints = thresholdLinePoints(suggested[zone]);
      if (suggestedPoints) {
        markLineSegments.push({
          name: `${zoneLabel(zone)} (${t('dashboard.workshop3.suggested')})`,
          lineStyle: { color: '#9CA3B8', width: 1, type: 'dashed' },
          label: {
            formatter: `${zoneLabel(zone)} (${t('dashboard.workshop3.suggested')})`,
            color: '#6B7280'
          },
          endpoints: suggestedPoints
        });
      }
    }
  });
  const scatterOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const point = scatterPoints.find((entry) => entry.name === params.name);
        const zone = point ? zoneLabel(point.zone) : '';
        const exposition = params.value[0];
        const fiabilite = params.value[1];
        const level = params.value[2];
        return `${params.name}<br />${t('dashboard.workshop3.expositionAxis')}: ${exposition}<br />`
          + `${t('dashboard.workshop3.fiabiliteAxis')}: ${fiabilite}<br />`
          + `${t('dashboard.workshop3.threatLevel')}: ${Number(level).toFixed(2)}<br />${zone}`;
      }
    },
    legend: {
      bottom: 0,
      data: ZONE_ORDER.map((zone) => zoneLabel(zone))
    },
    grid: { left: 8, right: 24, top: 40, bottom: 56, containLabel: true },
    xAxis: {
      type: 'value',
      name: t('dashboard.workshop3.expositionAxis'),
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { color: '#374151', fontWeight: 600, fontSize: 12 },
      min: 1,
      max: 16
    },
    yAxis: {
      type: 'value',
      name: t('dashboard.workshop3.fiabiliteAxis'),
      nameLocation: 'middle',
      nameGap: 36,
      nameTextStyle: { color: '#374151', fontWeight: 600, fontSize: 12 },
      min: 1,
      max: 16
    },
    series: [
      ...ZONE_ORDER.map((zone) => ({
        name: zoneLabel(zone),
        type: 'scatter',
        data: scatterPoints
          .filter((point) => point.zone === zone)
          .map((point) => ({ name: point.name || t('dashboard.workshop3.unnamed'), value: [point.exposition, point.fiabilite, point.threatLevel] })),
        itemStyle: { color: ZONE_COLORS[zone] },
        symbolSize: (value) => Math.max(8, Math.min(28, 8 + Number(value[2] || 0) * 4))
      })),
      {
        name: t('dashboard.workshop3.thresholdLines'),
        type: 'scatter',
        data: [],
        markLine: {
          silent: true,
          symbol: 'none',
          label: { show: true, position: 'insideEndTop', fontSize: 10 },
          data: markLineSegments
            .filter((segment) => segment.endpoints)
            .map((segment) => ([
              { coord: segment.endpoints[0], lineStyle: segment.lineStyle, label: segment.label },
              { coord: segment.endpoints[1] }
            ]))
        }
      }
    ]
  };

  // 3.2 Initial vs residual grouped bars (residual engine, top 15 + escape hatch).
  const residualRows = buildInitialResidualRows(scopedStakeholders, attackPaths, measures, liveThresholds);
  const topRows = selectTopByInitial(residualRows, TOP_INITIAL_LIMIT);
  const visibleRows = showAllResidual ? sortByReductionDesc(residualRows) : sortByReductionDesc(topRows);
  const isCapped = !showAllResidual && residualRows.length > TOP_INITIAL_LIMIT;

  const exportResidualCsv = () => {
    const header = ['stakeholder', 'initial', 'residual', 'reductionPct', 'initialZone', 'residualZone'];
    const lines = sortByReductionDesc(residualRows).map((row) => [
      `"${String(row.name || '').replace(/"/g, '""')}"`,
      row.initialRisk.toFixed(2),
      row.residualRisk.toFixed(2),
      row.reductionPct,
      row.initialZone,
      row.residualZone
    ].join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'workshop3-initial-residual.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const residualOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const row = visibleRows.find((entry) => entry.name === params[0].name);
        if (!row) {
          return params[0].name;
        }
        return `${row.name}<br />${t('dashboard.workshop3.initial')}: ${row.initialRisk.toFixed(2)} (${zoneLabel(row.initialZone)})<br />`
          + `${t('dashboard.workshop3.residual')}: ${row.residualRisk.toFixed(2)} (${zoneLabel(row.residualZone)})<br />`
          + `${t('dashboard.workshop3.reduction')}: ${row.reductionPct}%`;
      }
    },
    legend: {
      bottom: 0,
      data: [t('dashboard.workshop3.initial'), t('dashboard.workshop3.residual')]
    },
    grid: { left: 8, right: 24, top: 8, bottom: 56, containLabel: true },
    xAxis: { type: 'value' },
    yAxis: {
      type: 'category',
      data: visibleRows.map((row) => row.name),
      inverse: true,
      axisLabel: {
        interval: 0,
        formatter: (value) => (String(value).length > 24 ? `${String(value).slice(0, 24)}...` : String(value))
      }
    },
    series: [
      {
        name: t('dashboard.workshop3.initial'),
        type: 'bar',
        data: visibleRows.map((row) => ({
          value: Number(row.initialRisk.toFixed(2)),
          itemStyle: { color: ZONE_COLORS[row.initialZone] }
        }))
      },
      {
        name: t('dashboard.workshop3.residual'),
        type: 'bar',
        data: visibleRows.map((row) => ({
          value: Number(row.residualRisk.toFixed(2)),
          itemStyle: { color: ZONE_COLORS[row.residualZone], borderRadius: [0, 6, 6, 0] }
        }))
      }
    ]
  };

  // 3.3 Zone x origin stacked bar (replaces the zone + internal/external donuts).
  const zoneOrigin = buildZoneOriginBreakdown(scopedStakeholders, liveThresholds);
  const zoneOriginOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const row = zoneOrigin.find((entry) => zoneLabel(entry.zone) === params[0].name);
        if (!row) {
          return params[0].name;
        }
        return `${params[0].name}<br />${t('dashboard.workshop3.internal')}: ${row.internal}<br />`
          + `${t('dashboard.workshop3.external')}: ${row.external}<br />Total: ${row.total}`;
      }
    },
    legend: {
      bottom: 0,
      data: [t('dashboard.workshop3.internal'), t('dashboard.workshop3.external')]
    },
    grid: { left: 8, right: 24, top: 8, bottom: 56, containLabel: true },
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: {
      type: 'category',
      data: zoneOrigin.map((row) => zoneLabel(row.zone)),
      inverse: true,
      axisLabel: { interval: 0 }
    },
    series: [
      {
        name: t('dashboard.workshop3.internal'),
        type: 'bar',
        stack: 'total',
        data: zoneOrigin.map((row) => ({
          value: row.internal,
          itemStyle: { color: '#6366F1' }
        }))
      },
      {
        name: t('dashboard.workshop3.external'),
        type: 'bar',
        stack: 'total',
        data: zoneOrigin.map((row) => ({
          value: row.external,
          itemStyle: { color: '#06B6D4', borderRadius: [0, 6, 6, 0] }
        }))
      }
    ]
  };

  // 3.4 Priority x status heatmap (measures) with diverging visualMap.
  const heatmap = buildPriorityStatusMatrix(measures);
  const heatmapRowLabels = heatmap.rowLabels.map((priority) => priorityLabel(priority));
  const heatmapColumnLabels = heatmap.columnLabels.map((status) => statusLabel(status));
  const heatmapOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const priority = heatmap.rowLabels[params.value[1]];
        const status = heatmap.columnLabels[params.value[0]];
        const cell = heatmap.data.find(
          (entry) => entry.value[0] === params.value[0] && entry.value[1] === params.value[1]
        );
        return `${priorityLabel(priority)} • ${statusLabel(status)}: ${cell ? cell.count : 0}`;
      }
    },
    grid: { left: 8, right: 16, top: 8, bottom: 72, containLabel: true },
    xAxis: {
      type: 'category',
      data: heatmapColumnLabels,
      axisLabel: { interval: 0, rotate: 20 }
    },
    yAxis: {
      type: 'category',
      data: heatmapRowLabels,
      axisLabel: { interval: 0 }
    },
    visualMap: {
      type: 'piecewise',
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      pieces: (() => {
        const third = Math.max(1, heatmap.maxScore / 3);
        return [
          { value: 0, label: t('dashboard.workshop3.heatmapNone'), color: '#ECFDF5' },
          { min: 1, max: third, label: t('dashboard.workshop3.heatmapLow'), color: '#A7F3D0' },
          { min: third, max: third * 2, label: t('dashboard.workshop3.heatmapWatch'), color: '#F59E0B' },
          { min: third * 2, label: t('dashboard.workshop3.heatmapAlarm'), color: '#DC2626' }
        ];
      })()
    },
    series: [{
      type: 'heatmap',
      data: heatmap.data.map((cell) => ({ value: cell.value })),
      label: {
        show: true,
        formatter: (params) => {
          const cell = heatmap.data.find(
            (entry) => entry.value[0] === params.value[0] && entry.value[1] === params.value[1]
          );
          return cell && cell.count > 0 ? String(cell.count) : '';
        },
        color: '#111827'
      },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
    }]
  };

  // 3.5 Stretch: effectiveness radar (top stakeholders by initial threat).
  const effectivenessProfiles = buildEffectivenessProfiles(scopedStakeholders, attackPaths, measures, 5);
  const hasEffectiveness = effectivenessProfiles.some((profile) => profile.measureCount > 0);
  const radarMax = Math.max(
    4,
    Math.ceil(Math.max(0, ...effectivenessProfiles.flatMap((profile) => [profile.d, profile.p, profile.m, profile.c])))
  );
  const radarOption = {
    tooltip: { trigger: 'item' },
    legend: {
      bottom: 0,
      data: effectivenessProfiles.map((profile) => profile.name)
    },
    radar: {
      indicator: [
        { name: t('dashboard.workshop3.effectivenessD'), max: radarMax },
        { name: t('dashboard.workshop3.effectivenessP'), max: radarMax },
        { name: t('dashboard.workshop3.effectivenessM'), max: radarMax },
        { name: t('dashboard.workshop3.effectivenessC'), max: radarMax }
      ]
    },
    series: [{
      type: 'radar',
      data: effectivenessProfiles.map((profile) => ({
        name: profile.name,
        value: [
          Number(profile.d.toFixed(2)),
          Number(profile.p.toFixed(2)),
          Number(profile.m.toFixed(2)),
          Number(profile.c.toFixed(2))
        ]
      }))
    }]
  };

  if (stakeholdersLoading || measuresLoading) {
    return (
      <Card className="!bg-white">
        <Flex justifyContent="center" alignItems="center" className="h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent mr-3"></div>
          <Text>{t('dashboard.status.loading')}</Text>
        </Flex>
      </Card>
    );
  }

  if (totalCount === 0) {
    return (
      <Card className="!bg-white">
        <Flex justifyContent="center" alignItems="center" className="h-40 flex-col">
          <Users className="w-12 h-12 text-gray-300 mb-3" />
          <Text className="!text-gray-500">{t('dashboard.workshop3.noStakeholders')}</Text>
          <Text className="text-xs !text-gray-400 mt-1">{t('dashboard.workshop3.completeWorkshop3')}</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
      {/* KPI Cards Row */}
      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <Card decoration="top" decorationColor="teal" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop3.stakeholders')}</Text>
              <Metric className="!text-gray-900">{totalStakeholders}</Metric>
              <Text className="mt-1 text-xs !text-gray-500">
                {internalCount} {t('dashboard.workshop3.internal').toLowerCase()} / {externalCount} {t('dashboard.workshop3.external').toLowerCase()} • {categoryCount} {t('dashboard.workshop3.categories').toLowerCase()}
              </Text>
            </div>
            <div className="p-2 bg-teal-100 rounded-lg">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
          </Flex>
        </Card>

        <Card decoration="top" decorationColor="rose" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop3.dangerZone')}</Text>
              <Metric className="!text-gray-900">{dangerCount}</Metric>
              {dangerCount > 0 && (
                <BadgeDelta deltaType="increase" size="xs" className="mt-1">
                  {t('dashboard.workshop3.highRisk')}
                </BadgeDelta>
              )}
            </div>
            <div className="p-2 bg-rose-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
          </Flex>
        </Card>

        <Card decoration="top" decorationColor="emerald" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop3.retained')}</Text>
              <Metric className="!text-gray-900">{retainedCount}</Metric>
              <Flex className="mt-1" justifyContent="start" alignItems="center">
                <Badge color="emerald" size="xs">
                  {totalCount > 0 ? Math.round((retainedCount / totalCount) * 100) : 0}% {t('dashboard.workshop3.ofTotal')}
                </Badge>
              </Flex>
            </div>
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
          </Flex>
        </Card>

        <Card decoration="top" decorationColor="cyan" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop3.measures')}</Text>
              <Metric className="!text-gray-900">{measures.length}</Metric>
              <Text className="mt-1 text-xs !text-gray-500">
                {implementedCount} {t('dashboard.workshop3.statusImplemented').toLowerCase()}
                {orphanHealth.orphanCount > 0 && (
                  <span className="text-amber-600 font-medium">
                    {' '}• {orphanHealth.orphanCount} {t('dashboard.workshop3.orphans').toLowerCase()}
                  </span>
                )}
              </Text>
            </div>
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Shield className="w-6 h-6 text-cyan-600" />
            </div>
          </Flex>
        </Card>
      </Grid>

      {/* Charts Grid (draggable/resizable in edit mode) */}
      <DashboardGrid
        items={[
          {
            id: 'w3-scatter',
            fullscreenTitle: t('dashboard.workshop3.scatterTitle'),
            fullscreenContent: (<EChart option={scatterOption} height={560} notMerge />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Flex alignItems="start" justifyContent="between">
                  <div>
                    <Title className="!text-gray-900">{t('dashboard.workshop3.scatterTitle')}</Title>
                    <Text className="text-xs !text-gray-500 mt-1">
                      {t('dashboard.workshop3.scatterThresholds', {
                        danger: liveThresholds.danger,
                        control: liveThresholds.control,
                        watch: liveThresholds.watch
                      })}
                    </Text>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setRetainedOnly(true)}
                      className={`px-3 py-1 rounded-md text-xs font-medium ${retainedOnly ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {t('dashboard.workshop3.retainedOnly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRetainedOnly(false)}
                      className={`px-3 py-1 rounded-md text-xs font-medium ${!retainedOnly ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {t('dashboard.workshop3.showAllScope')}
                    </button>
                  </div>
                </Flex>
                {scatterPoints.length > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 95 }}><EChart option={scatterOption} height="100%" notMerge /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop3.noStakeholders')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w3-initialResidual',
            fullscreenTitle: t('dashboard.workshop3.initialResidualTitle'),
            fullscreenContent: (<EChart option={residualOption} height={560} notMerge />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Flex alignItems="start" justifyContent="between">
                  <div>
                    <Title className="!text-gray-900">{t('dashboard.workshop3.initialResidualTitle')}</Title>
                    <Text className="text-xs !text-gray-500 mt-1">
                      {isCapped
                        ? t('dashboard.workshop3.top15note', { count: residualRows.length })
                        : t('dashboard.workshop3.sortedByReduction')}
                    </Text>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {residualRows.length > TOP_INITIAL_LIMIT && (
                      <button
                        type="button"
                        onClick={() => setShowAllResidual((value) => !value)}
                        className="px-3 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                      >
                        {showAllResidual
                          ? t('dashboard.workshop3.showTop15')
                          : t('dashboard.workshop3.showAllCount', { count: residualRows.length })}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={exportResidualCsv}
                      className="px-3 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      {t('dashboard.workshop3.exportCsv')}
                    </button>
                  </div>
                </Flex>
                {visibleRows.length > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: Math.max(100, visibleRows.length * 22 + 40) }}><EChart
                    option={residualOption}
                    height="100%"
                    notMerge
                  /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop3.noStakeholders')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w3-zoneOrigin',
            fullscreenTitle: t('dashboard.workshop3.zoneOriginTitle'),
            fullscreenContent: (<EChart option={zoneOriginOption} height={560} notMerge />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop3.zoneOriginTitle')}</Title>
                {totalStakeholders > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={zoneOriginOption} height="100%" notMerge /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop3.noStakeholders')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w3-priorityHeatmap',
            fullscreenTitle: t('dashboard.workshop3.heatmapTitle'),
            fullscreenContent: (<EChart option={heatmapOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop3.heatmapTitle')}</Title>
                <Text className="text-xs !text-gray-500 mt-1">
                  {heatmap.unclassified > 0
                    ? t('dashboard.workshop3.unclassifiedMeasures', { count: heatmap.unclassified })
                    : t('dashboard.workshop3.heatmapSubtitle')}
                </Text>
                {orphanHealth.orphanCount > 0 && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <Text className="text-xs !text-amber-800">
                      {t('dashboard.workshop3.orphanBanner', {
                        count: orphanHealth.orphanCount,
                        total: orphanHealth.total
                      })}
                    </Text>
                  </div>
                )}
                {heatmap.total > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 95 }}><EChart option={heatmapOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop3.noMeasures')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          ...(hasEffectiveness ? [{
            id: 'w3-radar',
            fullscreenTitle: t('dashboard.workshop3.radarTitle'),
            fullscreenContent: (<EChart option={radarOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop3.radarTitle')}</Title>
                <Text className="text-xs !text-gray-500 mt-1">{t('dashboard.workshop3.radarSubtitle')}</Text>
                <div className="flex-1 min-h-0" style={{ minHeight: 95 }}><EChart option={radarOption} height="100%" /></div>
              </Card>
            )
          }] : [])
        ]}
        defaultLayout={W3_DEFAULT_LAYOUT}
        editMode={editMode}
        layouts={layouts}
        onLayoutChange={onLayoutChange}
        onToggleHidden={onToggleHidden}
      />
    </div>
  );
};

export default Workshop3Dashboard;
